/**
 * dev-host.ts — is this page served from the developer's own machine?
 *
 * Several modules take a backend base URL from a query param so a local editor
 * can be pointed at a local backend. Those requests carry the user's session
 * (Authorization: Bearer + credentials:'include'), and the sign-in dialog POSTs
 * the user's password to the auth base — so an override honoured in production
 * would let a crafted link (editor.olloport.com/?scene=x&authBase=https://evil)
 * make our own origin hand a real session and a real password to a third party.
 * The URL bar would still read editor.olloport.com throughout.
 *
 * The gate is `location.hostname`: the browser reports where this code was
 * actually served from, and neither a link nor page code can change it. So the
 * override survives only where it's useful (localhost) and is inert everywhere
 * a victim could be sent.
 *
 * Deliberately an exact match against a fixed list — never a suffix/substring
 * test on the supplied URL. "Does this look like one of ours" is the check that
 * loses to olloport.com.evil.tld; not accepting the input at all cannot.
 */

const DEV_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);

/** True only when the editor itself is running on the local machine. */
export const isDevHost = DEV_HOSTS.has(window.location.hostname);

/**
 * Read a base-URL override that is honoured ONLY in local dev; in production
 * the fallback is the sole possible value. Trailing slashes are trimmed so
 * callers can concatenate paths without doubling up.
 *
 * `||` (not `??`) so an empty `?authBase=` also falls back rather than
 * producing requests against an empty origin.
 */
export const devOnlyBase = (params: URLSearchParams, name: string, fallback: string): string => {
    return ((isDevHost && params.get(name)) || fallback).replace(/\/+$/, '');
};
